import { useMemo } from "react";

import * as Cmp from "@impero-com/common/utils/comparison";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    renderMarkdown,
    TST_BLESSED_ID_COLUMN_SELECTION,
    TST_COLUMN_SIZE,
    TST_DEFAULT_PAGE_SIZE,
} from "@impero-com/ui";
import {
    FilterTarget,
    usePresetTableSearchParams,
    ZodCodecMapToType,
} from "@impero-com/ui/domain";
import * as Sentry from "@sentry/react";
import {
    createColumnHelper,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
    getFacetedUniqueValues,
    Column,
    getPaginationRowModel,
} from "@tanstack/react-table";
import { Checkbox } from "antd";
import { Link } from "react-router-dom";

import {
    EntityOverviewListDto,
    EntityListLinkedResources,
    EntityOverview,
} from "api/generated/model";
import { AccessRightTags } from "components/accessRightTags";
import { toUsersByAccessRights } from "components/accessRightTags/utils";
import {
    AccessRightsFilterInput,
    AccessRightsFilterInputValue,
} from "components/table/AccessRightsFilterInput/AccessRightsFilterInput";
import { accessRightsFilterFunction } from "components/table/AccessRightsFilterInput/utils";
import { TSTValidTagsSelectFilter } from "components/table/tsTable/TSTValidTagsSelectFilter";
import { TagAndCategoryList } from "components/tag/tagAndCategory";
import {
    extractTagsAndCategoriesFromIds,
    extractUniqueTagIds,
    formatTagsAndCategoriesString,
    tagsTSTSortingFn,
} from "org/tags/tagsTSTUtils";
import { WebRoute } from "routes";
import { mapAsResourceWithoutAction } from "utils/dtoMapping";
import { createFilterFnWithMode } from "utils/table/createFilterFnWithMode";
import { createFilterHelpersGetter } from "utils/table/getTypedFilterValue";

import {
    CONTROL_ENTITIES_LIST_COLUMN_IDS as ID,
    CONTROL_ENTITIES_TABLE_STATE_STORAGE_KEY,
    controlEntitiesListColumnFiltersCodecMap,
    controlEntitiesListTableDefaultValues,
} from "./consts";

type ControlEntitiesListColumnFilters = ZodCodecMapToType<
    typeof controlEntitiesListColumnFiltersCodecMap
>;

type ControlEntitiesColumnId = (typeof ID)[keyof typeof ID];

export function useControlEntitiesListTable(data: EntityOverviewListDto) {
    const { t } = useTranslation();
    const linkedResources: EntityListLinkedResources = data.linkedResources;
    const columns = buildColumns(t, linkedResources);

    const tableData = useMemo(
        () => mapAsResourceWithoutAction(data.content),
        [data]
    );
    const notificationLabel = t("Table_RemovedFiltersWarning");
    const stateAndOnChanges = usePresetTableSearchParams(
        CONTROL_ENTITIES_TABLE_STATE_STORAGE_KEY,
        controlEntitiesListTableDefaultValues,
        controlEntitiesListColumnFiltersCodecMap,
        notificationLabel,
        Sentry.captureException
    );

    const table = useReactTable({
        ...stateAndOnChanges,
        data: tableData,
        columns,
        getRowId: (row) => row.id.toString(),
        initialState: {
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
                pageIndex: 0,
            },
        },
        enableSortingRemoval: false,
        getCoreRowModel: getCoreRowModel<EntityOverview>(),
        getSortedRowModel: getSortedRowModel<EntityOverview>(),
        getFilteredRowModel: getFilteredRowModel<EntityOverview>(),
        getFacetedMinMaxValues: getFacetedMinMaxValues<EntityOverview>(),
        getFacetedUniqueValues: getFacetedUniqueValues<EntityOverview>(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const getFilterComponentWrapper = (
        column: Column<EntityOverview>,
        target: FilterTarget
    ) => getFilterComponent(column, target, linkedResources, t);

    return {
        table,
        getFilterComponent: getFilterComponentWrapper,
        canAddException: (column: Column<EntityOverview>) =>
            column.id === ID.TAGS,
        tableId: CONTROL_ENTITIES_TABLE_STATE_STORAGE_KEY,
    };
}

function buildColumns(
    t: TFunction,
    linkedResources: EntityListLinkedResources
) {
    const columnHelper = createColumnHelper<EntityOverview>();
    const { users } = linkedResources;

    return [
        columnHelper.display({
            id: TST_BLESSED_ID_COLUMN_SELECTION,
            header: (info) => (
                <Checkbox
                    checked={info.table.getIsAllPageRowsSelected()}
                    indeterminate={info.table.getIsSomePageRowsSelected()}
                    onChange={info.table.getToggleAllRowsSelectedHandler()}
                />
            ),
            size: TST_COLUMN_SIZE.CHECKBOX,
            meta: { fixedWidth: true },
            cell: (info) => (
                <Checkbox
                    checked={info.row.getIsSelected()}
                    onClick={() => info.row.toggleSelected()}
                />
            ),
        }),
        columnHelper.accessor((row) => row.name, {
            id: ID.NAME,
            header: t("Entity_Label_Name"),
            cell: (info) => {
                const entityId = info.row.original.id;
                return (
                    <Link to={`${WebRoute.ENTITIES}/${entityId}`}>
                        {info.getValue()}
                    </Link>
                );
            },
            sortingFn: "alphanumeric",
            enableColumnFilter: false,
            size: TST_COLUMN_SIZE.L,
        }),
        columnHelper.accessor((row) => row.description || "", {
            id: ID.DESCRIPTION,
            header: t("Entity_Label_Description"),
            cell: (info) => {
                return <>{renderMarkdown(info.getValue())}</>;
            },
            sortingFn: "alphanumeric",
            enableColumnFilter: false,
            size: TST_COLUMN_SIZE.L,
        }),
        columnHelper.accessor(
            (row) =>
                // We need a string value for the global filter/search bar
                formatTagsAndCategoriesString(row.tagIds, {
                    tags: linkedResources.tags,
                    tagCategories: linkedResources.tagCategories,
                }),
            {
                id: ID.TAGS,
                header: t("Entity_Label_Tags"),
                cell: (info) => {
                    const { tags, tagCategories } =
                        extractTagsAndCategoriesFromIds(
                            info.row.original.tagIds,
                            linkedResources.tags,
                            linkedResources.tagCategories
                        );
                    return (
                        <TagAndCategoryList
                            tags={tags}
                            tagCategories={tagCategories}
                        />
                    );
                },
                sortingFn: (rowA, rowB) =>
                    tagsTSTSortingFn(
                        rowA.original.tagIds,
                        rowB.original.tagIds
                    ),
                filterFn: createFilterFnWithMode<EntityOverview, string[]>(
                    (row, value) => {
                        const rowTagIds = row.original.tagIds;
                        return value.some((tagId) => rowTagIds.includes(tagId));
                    }
                ),
                enableColumnFilter: true,
                size: TST_COLUMN_SIZE.M,
            }
        ),
        columnHelper.accessor((row) => row.controlAccessRights, {
            id: ID.CONTROL_ACCESS_RIGHTS,
            header: t("Entity_Column_Header_ControlAccessRights"),
            cell: (info) => {
                return (
                    <AccessRightTags
                        usersByAccessRight={toUsersByAccessRights(
                            info.getValue(),
                            users
                        )}
                    />
                );
            },
            sortingFn: Cmp.map(
                (row) =>
                    Object.values(row.original.controlAccessRights).reduce(
                        (acc, value) => acc + value.length,
                        0
                    ),
                Cmp.primitive
            ),
            filterFn: createFilterFnWithMode<
                EntityOverview,
                AccessRightsFilterInputValue
            >((row, value) =>
                accessRightsFilterFunction(
                    row.original.controlAccessRights,
                    value
                )
            ),
            enableColumnFilter: true,
            size: TST_COLUMN_SIZE.L,
        }),
        columnHelper.accessor((row) => row.riskAccessRights, {
            id: ID.RISK_ACCESS_RIGHTS,
            header: t("Entity_Column_Header_RiskAccessRights"),
            cell: (info) => {
                return (
                    <AccessRightTags
                        usersByAccessRight={toUsersByAccessRights(
                            info.getValue(),
                            users
                        )}
                    />
                );
            },
            sortingFn: Cmp.map(
                (row) =>
                    Object.values(row.original.riskAccessRights).reduce(
                        (acc, value) => acc + value.length,
                        0
                    ),
                Cmp.primitive
            ),
            filterFn: createFilterFnWithMode<
                EntityOverview,
                AccessRightsFilterInputValue
            >((row, value) =>
                accessRightsFilterFunction(row.original.riskAccessRights, value)
            ),
            enableColumnFilter: true,
            size: TST_COLUMN_SIZE.M,
        }),
    ];
}

function getFilterComponent(
    column: Column<EntityOverview>,
    target: FilterTarget,
    linkedResources: EntityListLinkedResources,
    t: TFunction
) {
    const getFilterHelpers =
        createFilterHelpersGetter<ControlEntitiesListColumnFilters>(column);
    const columnId = column.id as ControlEntitiesColumnId;

    switch (columnId) {
        case ID.NAME:
        case ID.DESCRIPTION:
            throw new Error(
                "Name and Description should be marked as not filterable"
            );
        case ID.TAGS: {
            const rows = column.getFacetedRowModel();
            const uniqueTagIds = extractUniqueTagIds(
                rows.flatRows.map((row) => row.original),
                (row) => row.tagIds
            );
            const { value, setFilterValue } = getFilterHelpers(
                columnId,
                target
            );
            return (
                <TSTValidTagsSelectFilter
                    tagCategories={linkedResources.tagCategories}
                    tags={linkedResources.tags}
                    uniqueTagIds={uniqueTagIds}
                    placeholder={t("Entity_TagFilter_Placeholder")}
                    setFilterValue={setFilterValue}
                    value={value}
                />
            );
        }
        case ID.CONTROL_ACCESS_RIGHTS: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            return (
                <AccessRightsFilterInput
                    users={linkedResources.users}
                    facetedValues={column.getFacetedUniqueValues()}
                    value={value}
                    onChange={(newValue) => {
                        setFilterValue(
                            newValue as unknown as AccessRightsFilterInputValue
                        );
                    }}
                />
            );
        }
        case ID.RISK_ACCESS_RIGHTS: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            return (
                <AccessRightsFilterInput
                    users={linkedResources.users}
                    facetedValues={column.getFacetedUniqueValues()}
                    value={value}
                    onChange={(newValue) => {
                        setFilterValue(
                            newValue as unknown as AccessRightsFilterInputValue
                        );
                    }}
                />
            );
        }
        default:
            assertNever(columnId);
    }
}
